import {
  dateForPdfName,
  formatProductModelForPdfName
} from "../../../helpers/Functions";

const ComparisonPageConfig = {
  collection: "comparison_pages",
  fields: [
    "id",
    "name",
    "template",
    "primary_product.id",
    "secondary_products.products_id.id",
    "variants.inherit_default_audience_pricing",
    "variants.pricing.price_types_id.id",
    "variants.inherit_default_audience_promos",
    "variants.promos.promos_id.id",
    "variants.inherit_default_audience_selected_features",
    "variants.selected_features.attributes_id.id",
    "variants.inherit_default_audience_gaptco",
    "variants.gaptco_savings",
    "variants.show_tco_graph",
    "variants.audience.key",
    "variants.locale.product_description_override",
    "variants.locale.title_override",
    "variants.locale.languages_code.code",
    //scanners
    "audience.inherit_default_audience_pricing",
    "audience.pricing.price_types_id.id",
    "audience.inherit_default_audience_promos",
    "audience.promos.promos_id.id",
    "audience.inherit_default_audience_selected_features",
    "audience.selected_features.attributes_id.id",
    "audience.inherit_default_audience_gaptco",
    "audience.gaptco_savings",
    "audience.show_tco_graph",
    "audience.audience.key",
    "audience.title_formatting",
    "audience.locale.product_description_override",
    "audience.locale.title_override"
  ],

  filter: "filter[status][_eq]=active&filter[template][_neq]=null",

  adapter: class ComparisonPage {
    constructor(comparison_page) {
      this.id = comparison_page.id;
      this.name = comparison_page.name;
      this.template = comparison_page.template || "";

      // Arbitrary ID name
      this.html_id = `comparison-${this.id}`;

      this.primary_product = comparison_page.primary_product?.id;
      this.secondary_products = (comparison_page.secondary_products || []).map(
        (ref) => ref?.products_id?.id
      );

      this.promos = {};
      this.pricing = {};
      this.selected_features = {};
      this.gaptco_savings = {};
      this.show_tco_graph = {};
      this.product_description_override = {};
      this.title_override = {};
      this.inherit_default_audience_pricing = {};
      this.inherit_default_audience_promos = {};
      this.inherit_default_audience_selected_features = {};
      this.inherit_default_audience_gaptco = {};

      // Preliminary to "audiencing" some fields, similar to localizing. Intended to index some fields by the audience key.
      (comparison_page.audience || comparison_page.variants || []).forEach(
        (aud) => {
          let selected_features = (aud.selected_features || [])
            .map((ref) => ref.attributes_id?.id)
            .filter((v) => !!v);
          let pricing = (aud.pricing || []).map((ref) => ref.price_types_id.id);
          let promos = (aud.promos || []).map((ref) => ref.promos_id.id);

          if (selected_features.length > 0) {
            this.selected_features[aud.audience.key] = selected_features;
          }
          if (pricing.length > 0) {
            this.pricing[aud.audience.key] = pricing;
          }
          if (promos.length > 0) {
            this.promos[aud.audience.key] = promos;
          }

          this.gaptco_savings[aud.audience.key] = aud.gaptco_savings;
          this.show_tco_graph[aud.audience.key] = aud.show_tco_graph;

          this.inherit_default_audience_pricing[aud.audience.key] =
            aud.inherit_default_audience_pricing || "inherit";
          this.inherit_default_audience_promos[aud.audience.key] =
            aud.inherit_default_audience_promos || "inherit";
          this.inherit_default_audience_selected_features[aud.audience.key] =
            aud.inherit_default_audience_selected_features || "inherit";
          this.inherit_default_audience_gaptco[aud.audience.key] =
            aud.inherit_default_audience_gaptco || "inherit";
          this.title_formatting = aud.title_formatting || "primary-model";
          let locale = aud.locale?.[0];
          this.product_description_override[aud.audience.key] =
            locale?.product_description_override;
          this.title_override[aud.audience.key] = locale?.title_override;
        }
      );
    }

    map(collections, maps) {
      // Collect default audience
      this.default_audience = collections.global_settings.default_audience;
      // We are "audiencing" some fields, similar to localization. Intended to index some fields by the audience key.
      collections.audiences.forEach(({ key }) => {
        if (this.promos[key]) {
          this.promos[key] = this.promos[key]
            .map((promo_id) => maps.promos[promo_id])
            .filter((v) => !!v);
        }
        if (this.pricing[key]) {
          this.pricing[key] = this.pricing[key]
            .map((pricing_id) => maps.price_types[pricing_id])
            .filter((v) => !!v);
        }
        if (this.selected_features[key]) {
          this.selected_features[key] = this.selected_features[key]
            .map((feature_id) => maps.attributes[feature_id])
            .filter((v) => !!v);
        }
      });

      collections.audiences.forEach(({ key }) => {
        this.inherit_default_audience_pricing[key] =
          this.inherit_default_audience_pricing[key] || "inherit";
        this.inherit_default_audience_promos[key] =
          this.inherit_default_audience_promos[key] || "inherit";
        this.inherit_default_audience_selected_features[key] =
          this.inherit_default_audience_selected_features[key] || "inherit";
        this.inherit_default_audience_gaptco[key] =
          this.inherit_default_audience_gaptco[key] || "inherit";
      });

      this.primary_product = maps.products[this.primary_product];
      this.secondary_products = this.secondary_products
        .map((product_id) => maps.products[product_id])
        // Retain valid products
        .filter((p) => !!p);

      this.template = maps.comparison_template_type[this.template].key;

      if (!!this.primary_product) {
        const all_products = [this.primary_product, ...this.secondary_products];
        const formatted_product_names = all_products.map((p) =>
          formatProductModelForPdfName(p)
        );
        const comparison_name = formatted_product_names.join("-");
        // <primary>-<secondary1>-<secondary2>-comparison_<date>.pdf
        const date = dateForPdfName();
        this.generated_name = `${comparison_name}-comparison_${date}.pdf`;
      }
    }
  }
};

export default ComparisonPageConfig;
